/**
 * Shared wire types and constants for free-tier landing research and `/chat` (`MAR-1215`,
 * `MAR-1216`, `MAR-1217`, `MAR-1218`, `MAR-1482`).
 *
 * Used by:
 *   - `GET /api/landing-research` (`LandingResearchStatusResponse`)
 *   - `PUT /api/profile` (`LANDING_RESEARCH_MARKETING_OPT_IN_TEXTS`)
 *   - `server/workers/public-rag/runners.ts` (`LandingResearchPrincipal`)
 */

export const MAX_LANDING_RESEARCH_QUESTION_LEN = 1000
export const LANDING_RESEARCH_JURISDICTION = 'SV' as const
export const LANDING_RESEARCH_ENGINE = 'gemma' as const

/**
 * The marketing consent copy, per hero language. THE SERVER STORES THIS, never text from the client
 * (`MAR-1215` F5): when `marketing_opt_in: true` is submitted on `POST /api/landing-research`,
 * `users.marketing_opt_in_text` gets the entry for the request's `language`, so the consent record is
 * the copy the user saw. The hero (`MAR-1216`) must render its checkbox label from this constant,
 * not from its own string, or the two drift.
 */
export const LANDING_RESEARCH_MARKETING_OPT_IN_TEXTS = {
  es: 'Acepto recibir comunicaciones comerciales de Marcus sobre productos y novedades legales.',
  en: 'I agree to receive marketing communications from Marcus about products and legal news.',
} as const satisfies Record<'es' | 'en', string>

/** The Spanish consent copy (the default language). */
export const LANDING_RESEARCH_MARKETING_OPT_IN_TEXT = LANDING_RESEARCH_MARKETING_OPT_IN_TEXTS.es

/**
 * The length of a question as the cap counts it: CHARACTERS (Unicode code points), not UTF-16 code
 * units, so an emoji or another astral character counts once (`MAR-1215` F6). The hero form
 * (`MAR-1216`) mirrors the cap with this same function.
 */
export function landingResearchQuestionLength(question: string): number {
  return [...question].length
}

/**
 * `invite_pending` (MAR-1452 PR-8): an org-less user with an OPEN invite to their email. They get no
 * free use until they accept it (`free_tier_invite_pending`), so the status says so before they type —
 * the composer and /chat show `FREE_TIER_INVITE_PENDING_MESSAGES` and send nothing.
 */
export const LANDING_RESEARCH_STATES = ['app', 'available', 'exhausted', 'unverified', 'cap_reached', 'invite_pending'] as const
export type LandingResearchState = typeof LANDING_RESEARCH_STATES[number]

/**
 * Where `/chat` sends a user it refuses as `unverified` (MAR-1443). The query flag tells the landing
 * composer to open on its "Confirma tu correo" view even with no question pending; without it the
 * user saw the plain composer and no reason for the bounce.
 */
export const LANDING_VERIFY_EMAIL_QUERY = 'verify-email'
export const LANDING_VERIFY_EMAIL_PATH = `/?${LANDING_VERIFY_EMAIL_QUERY}=1#investigar`

export const LANDING_RESEARCH_REFUSAL_CODES = [
  'use_app',
  'email_unverified',
  'in_flight',
  'usage_limit_reached',
  'cap_reached',
  'free_tier_abuse_blocked',
  'free_tier_invite_pending',
] as const

/**
 * What a user with an OPEN invite to their email is told when they try the free tier (MAR-1452 PR-7a,
 * decided by Alex 2026-11-01). They get no trial organization — so they can still join the one that
 * invited them — and, with the org-less free path retired, no free use either until they accept. One
 * copy for every surface that refuses them: `/chat` streams it as the turn's answer
 * (`freeTierInvitePendingMessage`), the plan gate's `403 free_tier_invite_pending` carries it, and the
 * landing composer and /chat render it for `GET`'s `invite_pending` state before anything is sent.
 */
export const FREE_TIER_INVITE_PENDING_CODE = 'free_tier_invite_pending' as const satisfies LandingResearchRefusalCode
export const FREE_TIER_INVITE_PENDING_MESSAGES = {
  es: 'Tienes una invitación pendiente para unirte a una organización en Marcus. Acéptala desde el correo de invitación para empezar a usar Marcus: el chat gratuito no está disponible mientras tengas una invitación pendiente.',
  en: "You have a pending invitation to join an organization on Marcus. Accept it from the invitation email to start using Marcus — free chat isn't available while you have a pending invitation.",
} as const satisfies Record<'es' | 'en', string>
export type LandingResearchRefusalCode = typeof LANDING_RESEARCH_REFUSAL_CODES[number]

/** Shared Spanish copy for the public-chat monthly cap / waitlist state (`MAR-1371` AC1, AC2). */
export const PUBLIC_CHAT_CAP_HEADING = 'Estamos teniendo mucha demanda en este momento'
export const PUBLIC_CHAT_CAP_BODY = 'Hemos alcanzado el límite mensual de consultas gratuitas. Déjanos tu correo para unirte a la lista de espera y te avisaremos en cuanto abramos nuevos cupos.'
export const PUBLIC_CHAT_WAITLIST_SUCCESS = 'Te hemos anotado en la lista de espera. Te avisaremos por correo en cuanto abramos nuevos cupos.'

/** Shared Spanish copy for public-chat high-risk / blocked abuse defenses (`MAR-1372` AC2). */
export const PUBLIC_CHAT_ABUSE_BLOCKED_MESSAGE = 'Hemos detectado actividad inusual desde tu cuenta o conexión. Por seguridad, el acceso gratuito está temporalmente bloqueado. Si necesitas acceso profesional continuo, agenda una demostración o inicia sesión con tu organización.'
/**
 * What a free user the risk check LIMITED is told (MAR-1442), on every surface — `/chat`'s refusal
 * (`freeTierRiskLimitedMessage`) and the landing-research POST alike. Deliberately says nothing about
 * why: the old high-risk sentence named "cuentas nuevas o conexiones compartidas", which is the
 * scoring's own inputs handed to whoever wants to get around it.
 */
export const PUBLIC_CHAT_RISK_LIMITED_MESSAGES = {
  es: 'Las consultas gratuitas no están disponibles para esta cuenta por ahora. Vuelve a intentarlo más tarde — o mejora a Pro para seguir investigando sin interrupciones.',
  en: "Free questions aren't available for this account right now. Please try again later — or upgrade to Pro to keep researching without interruptions.",
} as const satisfies Record<'es' | 'en', string>

export interface LandingResearchWaitlistRequest {
  email: string
  question?: string | null
  locale?: string
}

export interface LandingResearchWaitlistResponse {
  ok: true
}

/**
 * Principal stored on a free-tier `public_rag_jobs` document (`MAR-1215` AC1, AC2): since MAR-1452 (D19)
 * a member on a `free_tier` seat in their free-trial organization, billed on an ordinary org ticket against
 * that seat. The org-less form (`scope: 'free'`, a free ticket) is retired (MAR-1452 PR-7a): a job still
 * carrying it from before the deploy is failed by the worker, never run or billed.
 */
export type LandingResearchPrincipal = { scope: 'org', organizationId: string, userId: string, freeTier: true }

export type LandingResearchLanguage = 'es' | 'en'

/** Whether one free allowance has anything left this month (MAR-1390 round 5). */
export type FreeAllowanceState = 'available' | 'exhausted'

/**
 * The free plan's two allowances, each on its own (MAR-1390 round 5): `chat` pays for Fast (Gemini),
 * `gemma` for Slow and for landing research. A free turn is gated on the allowance of its tier.
 */
export interface FreeAllowances {
  chat: FreeAllowanceState
  gemma: FreeAllowanceState
}

/**
 * WHY a free allowance reads spent (MAR-1442): `allowance` — the user really used it up; or
 * `risk_limited` — the public-chat risk check (MAR-1372) reduced or withheld it. The two are told
 * apart because telling someone with no usage that they "used" their limit is false. Only the reason
 * crosses the wire: never the score, the tier or the signals behind them.
 */
export type FreeLimitReason = 'allowance' | 'risk_limited'

/** Response body of `GET /api/landing-research` (`MAR-1215` AC4). */
export interface LandingResearchStatusResponse {
  /** Set whenever an allowance reads `exhausted` (never on `cap_reached`): why it does (MAR-1442). */
  limitReason?: FreeLimitReason
  /**
   * `exhausted` means NOTHING is left: both allowances are spent (or the caller is risk-blocked).
   * Since MAR-1390 round 5 one spent allowance is `available` here and shows in `allowances`.
   * `invite_pending` (MAR-1452 PR-8): an open invite awaits the org-less caller, so there is no free
   * use (and `percentLeft` is `0`) until they accept it.
   */
  state: LandingResearchState
  /** Each allowance on its own, for an enrolled free-tier caller (`available`/`exhausted` states). */
  allowances?: FreeAllowances
  /** Email of the unverified caller when `state === 'unverified'`. */
  email?: string
  /**
   * Remaining percentage (`0..100`) of the user's monthly free CHAT allowance (Fast) — what the
   * /chat banner shows (MAR-1390). Falls back to the Gemma allowance against a gateway that does not
   * report `chat` yet. `state`, not this, is what gates a send: it still follows the Gemma allowance.
   */
  percentLeft: number
  /** ISO 8601 timestamp when the current UTC monthly allowance resets (`YYYY-MM-01T00:00:00.000Z`). */
  resetsAt: string
}

export type LandingResearchStateResponse = LandingResearchStatusResponse

